import { useEffect, useState, lazy, Suspense } from 'react';
import { Route, Routes, useLocation } from 'react-router-dom';
import Header from '../components/header/Header';
import Footer from '../components/footer/Footer';
import 'swiper/swiper.min.css';
import '../assets/boxicons-2.0.7/css/boxicons.min.css';
import '../App.scss';
import Home from '../pages/Home';
import PrivateRoute from './PrivateRoute';
import { useCrawlEvents } from '../hooks/useCrawlEvents';
import { useHomeRefresh } from '../context/HomeRefreshContext';

// ── Lazy-load các route nặng (player hls.js, admin MUI/charts, watch-party…)
//    để KHÔNG nhồi vào bundle trang chủ. Mỗi route tải chunk riêng khi cần.
const DepositScreen          = lazy(() => import("../pages/payment/DepositScreen"));
const PaymentResult          = lazy(() => import("../pages/payment/PaymentResponse"));
const MovieDetail            = lazy(() => import("../pages/detail/MovieDetail"));
const WatchingMovie          = lazy(() => import("../pages/watching/WatchingMovie"));
const MovieGrid              = lazy(() => import("../components/movie-grid/MovieGrid"));
const FavoriteMovies         = lazy(() => import("../pages/FavoriteMovies"));
const VipPackages            = lazy(() => import("../pages/membership/VipPackages"));
const ActorGird              = lazy(() => import("../components/actor/ActorGrid"));
const ActorDetail            = lazy(() => import("../components/actor/ActorDetail"));
const GenreManagement        = lazy(() => import('../pages/admin/genre/GenreManagement'));
const AdminLayout            = lazy(() => import('../layouts/AdminLayout'));
const AdminDashboard         = lazy(() => import('../pages/admin/dashboard/AdminDashBoard'));
const MovieManagement        = lazy(() => import('../pages/admin/movie/MovieManagement'));
const CreateMovie            = lazy(() => import('../pages/admin/movie/create/CreateMovie'));
const TopicGenre             = lazy(() => import("../pages/TopicGenre"));
const AdminMovieDetail       = lazy(() => import('../pages/admin/movie/detail/AdminMovieDetail'));
const UserManagement         = lazy(() => import('../pages/admin/user/UserManagement'));
const ChatManager            = lazy(() => import("../pages/admin/ChatManager"));
const ResetPasswordPage      = lazy(() => import("../components/ResetPasswordPage"));
const CrawlMoviePage         = lazy(() => import("../pages/admin/crawl/CrawlMoviePage"));
const ReportManagement       = lazy(() => import('../pages/admin/report/ReportManagement'));
const RatingManagement       = lazy(() => import('../pages/admin/rating/RatingManagement'));
const SettingsManagement     = lazy(() => import('../pages/admin/settings/SettingsManagement'));
const HomeSectionsManagement = lazy(() => import('../pages/admin/home/HomeSectionsManagement'));
const WatchSectionsManagement= lazy(() => import('../pages/admin/watch/WatchSectionsManagement'));
const PrerollManagement      = lazy(() => import('../pages/admin/ads/PrerollManagement'));
const WatchHistory           = lazy(() => import('../pages/WatchHistory'));
const AccountPage            = lazy(() => import('../pages/account/AccountPage'));
const NotificationsPage      = lazy(() => import('../pages/NotificationsPage'));
const NotificationManagement = lazy(() => import('../pages/admin/notification/NotificationManagement'));
const TrailerPage            = lazy(() => import('../pages/TrailerPage'));
const CardShowcase           = lazy(() => import('../pages/CardShowcase'));
const CardShowcase2          = lazy(() => import('../pages/CardShowcase2'));
const UISettings             = lazy(() => import('../pages/admin/ui/UISettings'));
const WatchPartyPage         = lazy(() => import('../pages/watchparty/WatchPartyPage'));
const CreateRoomPage         = lazy(() => import('../pages/watchparty/CreateRoomPage'));
const WatchPartyRoom         = lazy(() => import('../pages/watchparty/WatchPartyRoom'));
const LivestreamPage         = lazy(() => import('../pages/livestream/LivestreamPage'));
const LivestreamManagement   = lazy(() => import('../pages/admin/livestream/LivestreamManagement'));
const RecordingManagement    = lazy(() => import('../pages/admin/recording/RecordingManagement'));

function ScrollToTop() {
    const { pathname } = useLocation();
    useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
    return null;
}

function PortraitLock({ enabled }) {
    const { pathname } = useLocation();
    const isWatchPage = pathname.startsWith('/watch/');
    const active = enabled && !isWatchPage;
    const [isLandscape, setIsLandscape] = useState(false);

    useEffect(() => {
        if (!active) { setIsLandscape(false); return; }
        const check = () => {
            const mobile = window.innerHeight <= 600 || ('ontouchstart' in window);
            setIsLandscape(mobile && window.innerWidth > window.innerHeight);
        };
        check();
        window.addEventListener('resize', check);
        window.addEventListener('orientationchange', check);
        return () => {
            window.removeEventListener('resize', check);
            window.removeEventListener('orientationchange', check);
        };
    }, [active]);

    if (!active || !isLandscape) return null;

    return (
        <div style={{
            position: 'fixed', inset: 0, zIndex: 99999,
            background: '#0d1117',
            display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center',
            color: '#fff', gap: 16,
        }}>
            <div style={{ fontSize: 48 }}>📱</div>
            <p style={{ fontSize: 16, fontWeight: 600, margin: 0 }}>Vui lòng xoay dọc màn hình</p>
            <p style={{ fontSize: 13, color: '#8b949e', margin: 0 }}>Trang này hiển thị tốt nhất ở chế độ dọc</p>
        </div>
    );
}

function ScrollToTopBtn() {
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        const onScroll = () => setVisible(window.scrollY > 300);
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    if (!visible) return null;

    return (
        <button
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            style={{
                position: 'fixed', bottom: 28, left: 24, zIndex: 9999,
                width: 44, height: 44, borderRadius: '50%',
                background: 'var(--ui-accent, #f5c518)',
                color: '#fff', border: 'none', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 20, boxShadow: '0 4px 16px rgba(0,0,0,0.4)',
                transition: 'opacity 0.2s',
            }}
            title="Lên đầu trang"
        >
            ↑
        </button>
    );
}

function RefreshHandler() {
    const { triggerRefresh } = useHomeRefresh();
    useCrawlEvents(triggerRefresh);
    return null;
}

function HomeWithRefresh() {
    const { refreshKey } = useHomeRefresh();
    return <Home refreshKey={refreshKey} />;
}

export function AppRouter() {
  const [portraitLockEnabled, setPortraitLockEnabled] = useState(true);

  useEffect(() => {
    fetch('/movies-settings-api.php')
      .then(r => r.json())
      .then(d => {
        if (!d.success) return;
        const map = {};
        d.data.forEach(r => { map[r.setting_key] = r.setting_value; });
        const accent = map['ui_accent_color'];
        if (accent) document.documentElement.style.setProperty('--ui-accent', accent);
        // Viền card (neon) bật/tắt từ admin: tắt → viền 0 + glow vàng trong suốt
        if (map['ui_neon_border'] === 'false') {
            document.documentElement.style.setProperty('--mc-neon-border', '0px');
            document.documentElement.style.setProperty('--mc-neon-glow', 'transparent');
        }
        if (map['portrait_lock_enabled'] !== undefined) setPortraitLockEnabled(map['portrait_lock_enabled'] !== 'false');
      })
      .catch(() => {});
  }, []);

  return (
    <>
    <ScrollToTop />
    <RefreshHandler />
    <PortraitLock enabled={portraitLockEnabled} />
    <ScrollToTopBtn />
    <Suspense fallback={<div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#888' }}>Đang tải...</div>}>
    <Routes>
      {/* User Routes with Header and Footer */}
      <Route
        path="/*"
        element={
          <>
            <Header />
            <Routes>
              <Route path="/" element={<HomeWithRefresh />} />
              <Route path="/detail/:slug" element={<MovieDetail />} />
              {/* Alias trang chi tiết: link share + canonical/og:url dùng /xem/{slug} (watch-ssr.php). Thêm /phim /movie cho khớp nginx SSR. */}
              <Route path="/xem/:slug" element={<MovieDetail />} />
              <Route path="/phim/:slug" element={<MovieDetail />} />
              <Route path="/movie/:slug" element={<MovieDetail />} />
              {/* Share theo tập: /xem/{slug}/{số tập} (ep số thường, link sạch) → mở thẳng player */}
              <Route path="/xem/:slug/:ep" element={<WatchingMovie />} />
              <Route path="/watch/:slug/:ep" element={<WatchingMovie />} />
              <Route path="/payment" element={<DepositScreen />} />
              <Route path="/result" element={<PaymentResult />} />
              <Route path="/favortie" element={<FavoriteMovies />} />
              <Route path="/membership" element={<VipPackages />} />
              <Route path="/catalog" element={<MovieGrid />} />
              <Route path="/search/:keyword" element={<MovieGrid />} />
              <Route path="/search/country/:nation" element={<MovieGrid />} />
              <Route path="/search/genre/:topic" element={<MovieGrid />} />
              <Route path="/actors" element={<ActorGird />} />
              <Route path="/genre" element={<TopicGenre />} />
              <Route path="/actor/practice/:key" element={<ActorDetail />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/history" element={<WatchHistory />} />
              <Route path="/account" element={<AccountPage />} />
              <Route path="/thong-bao" element={<NotificationsPage />} />
              <Route path="/trailer" element={<TrailerPage />} />
              <Route path="/cards" element={<CardShowcase />} />
              <Route path="/cards2" element={<CardShowcase2 />} />
              <Route path="/xem-chung" element={<WatchPartyPage />} />
              <Route path="/xem-chung/tao-phong" element={<CreateRoomPage />} />
              <Route path="/livestream" element={<LivestreamPage />} />
            </Routes>
            <Footer />
          </>
        }
      />
      {/* Watch Party Room — chỉ có Header, không có Footer */}
      <Route
        path="/xem-chung/phong/:roomId"
        element={
          <>
            <Header />
            <WatchPartyRoom />
          </>
        }
      />

      <Route
        path="/admin/*"
        element={
          <PrivateRoute >
            <AdminLayout>
              <Routes>
                <Route path="/dashboard" element={<AdminDashboard />} />
                <Route path="/genres" element={<GenreManagement />} />
                <Route path="/movies" element={<MovieManagement />} />
                <Route path="/movies/create" element={<CreateMovie />} />
                <Route path="/movies/detail/:slug" element={<AdminMovieDetail />} />
                <Route path="/users" element={<UserManagement />} />
                <Route path="/messages" element={<ChatManager />} />
                <Route path="/crawl" element={<CrawlMoviePage />} />
                <Route path="/reports" element={<ReportManagement />} />
                <Route path="/ratings" element={<RatingManagement />} />
                <Route path="/settings" element={<SettingsManagement />} />
                <Route path="/home-sections" element={<HomeSectionsManagement />} />
                <Route path="/watch-sections" element={<WatchSectionsManagement />} />
                <Route path="/preroll-ads" element={<PrerollManagement />} />
                <Route path="/ui-settings" element={<UISettings />} />
                <Route path="/notifications" element={<NotificationManagement />} />
                <Route path="/livestream" element={<LivestreamManagement />} />
                <Route path="/recordings" element={<RecordingManagement />} />
              </Routes>
            </AdminLayout>
          </PrivateRoute>
        }
      />
    </Routes>
    </Suspense>
    </>
  );
}